import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Ruler } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PropertyMapPanel from "@/components/PropertyMapPanel";
import { resolveMeasurementProvider, PROVIDER_GOOGLE } from "@/lib/measurementProvider";
import MeasurementWorkspace from "@/pages/MeasurementWorkspace";

/**
 * LineMeasureDialog
 * -----------------
 * Per-line inline map measurement (QuoteIQ-style). Reuses PropertyMapPanel
 * (satellite + geocoder + polygon/line draw + zoom-to-property) and, on
 * save, persists a measurement under the current property, then hands the
 * caller `{ measurement_id, output_value }` so the line's quantity/area
 * field can be pre-filled and the estimate total recalculated.
 *
 * Modes:
 *  - Create: no `existingMeasurement` — POST /api/estimator/measurements
 *  - Edit:   `existingMeasurement` provided — PUT the same id, keeping the
 *            same measurement_id so any linked line stays linked (historical
 *            snapshots on estimates/jobs/invoices remain valid).
 *
 * Feature type is derived from the calculator's input_unit:
 *  - "linear_ft" → line drawing, use lengthFt
 *  - anything else → polygon, use areaSqFt
 */
export default function LineMeasureDialog({
  open,
  onClose,
  propertyId,
  propertyAddress,
  propertyLat,
  propertyLng,
  calculatorKey,
  calculators = [],
  defaultLabel = "",
  existingMeasurement = null,
  onApply,
  // Iter D.1 — estimate context (optional, per-line entry point)
  estimateId,
  estimateLineItems,
  targetLineItemIndex,
  initialFocusMeasurementId,
}) {
  const calc = calculators.find((c) => c.key === calculatorKey);
  // input_unit is "ft" for linear-foot calculators, "sqft" for area, etc.
  // (The stale "linear_ft" check pre-Iter40 always evaluated false, which
  // silently forced every line into area-mode.)
  const isLinear = calc?.input_unit === "ft";
  const isEdit = Boolean(existingMeasurement?.id);

  // Backend stores measurement.geometry as raw {type, coordinates}. Mapbox
  // Draw needs a FeatureCollection for restoration — translate both ways.
  const rawGeomToFC = (g) => (
    g && g.type && g.coordinates
      ? { type: "FeatureCollection", features: [{ type: "Feature", geometry: g, properties: {} }] }
      : (g && g.type === "FeatureCollection" ? g : null)
  );
  const fcToRawGeom = (fc) => {
    if (!fc || !fc.features?.length) return null;
    return fc.features[0].geometry || null;
  };

  const [label, setLabel] = useState("");
  const [drawn, setDrawn] = useState({ areaSqFt: 0, lengthFt: 0, inferredAreaSqFt: 0, geojson: null });
  const [manualOverride, setManualOverride] = useState("");
  const [saving, setSaving] = useState(false);
  const [useGoogle, setUseGoogle] = useState(false);

  useEffect(() => {
    if (!open) return;
    let m = true;
    resolveMeasurementProvider({ force: true }).then((d) => { if (m) setUseGoogle(d.provider === PROVIDER_GOOGLE); });
    return () => { m = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setLabel(existingMeasurement?.label || defaultLabel || calc?.label || "Measurement");
    setDrawn({
      areaSqFt: !isLinear ? Number(existingMeasurement?.output_value || 0) : 0,
      lengthFt: isLinear ? Number(existingMeasurement?.output_value || 0) : 0,
      inferredAreaSqFt: 0,
      geojson: rawGeomToFC(existingMeasurement?.geometry),
    });
    setManualOverride("");
  }, [open, existingMeasurement, defaultLabel, isLinear, calc?.label]);

  // For area services (sqft) fall back to the "closed-line" inferred area
  // if the user picked the line-string tool by mistake. This is the fix for
  // the Iter40 report: "polygon drawn but Area stays 0 ft²" — most of the
  // time the caller actually drew a LineString.
  const drawnValue = isLinear
    ? drawn.lengthFt
    : (drawn.areaSqFt > 0 ? drawn.areaSqFt : (drawn.inferredAreaSqFt || 0));
  const effectiveValue = Number(manualOverride || 0) > 0 ? Number(manualOverride) : drawnValue;
  const unitLabel = isLinear ? "linear ft" : "ft²";

  const submit = async () => {
    if (!propertyId) { toast.error("Pick a property first."); return; }
    if (!effectiveValue || effectiveValue <= 0) {
      toast.error(`Draw on the map or enter a manual ${unitLabel} value.`);
      return;
    }
    setSaving(true);
    try {
      // Detect the actual drawn geometry — Mapbox Draw may return either a
      // Polygon (correct tool) or a LineString (user picked the line tool by
      // mistake). For sqft services we auto-close a LineString into a Polygon
      // so the saved geometry actually represents an area feature.
      let rawGeom = fcToRawGeom(drawn.geojson);
      if (!isLinear && rawGeom?.type === "LineString") {
        const c = rawGeom.coordinates || [];
        if (c.length >= 3) {
          const ring = [...c, c[0]];
          rawGeom = { type: "Polygon", coordinates: [ring] };
        }
      }
      const usedManual = Number(manualOverride || 0) > 0 && !rawGeom;
      const feature_type = usedManual ? "manual" : (isLinear ? "line" : "polygon");
      const payload = {
        property_id: propertyId,
        calculator_key: calculatorKey || (isLinear ? "linear_ft" : "sqft_area"),
        feature_type,
        label: label.trim() || defaultLabel || "Measurement",
        geometry: usedManual ? null : rawGeom,
        manual_value: usedManual ? Number(manualOverride) : (drawnValue || 0),
        calculator_params: {},
      };
      const url = isEdit
        ? `/estimator/measurements/${existingMeasurement.id}`
        : `/estimator/measurements`;
      const { data } = isEdit
        ? await api.put(url, payload)
        : await api.post(url, payload);
      toast.success(isEdit ? "Measurement updated" : "Measurement saved");
      onApply?.({
        measurement_id: data.id,
        output_value: data.output_value,
        label: data.label,
        measurement: data,
      });
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "";
      toast.error(`Save failed: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  // When Google Maps is enabled for this tenant, open the Google
  // MeasurementWorkspace instead of the Mapbox PropertyMapPanel. The
  // workspace persists to /api/measurements; we translate its saved
  // record into the shape LineMeasureDialog's caller expects
  // ({ measurement_id, output_value }) so the line-item integration and
  // estimate recalculation continue to work unchanged.
  if (open && useGoogle) {
    return (
      <MeasurementWorkspace
        key={`${propertyId || "none"}::${propertyAddress || ""}`}
        open={open}
        onClose={onClose}
        propertyAddress={propertyAddress}
        propertyLat={propertyLat}
        propertyLng={propertyLng}
        // Iter D.1 — pass estimate context so the workspace can link the
        // selected measurement(s) directly to the invoking line.
        estimateId={estimateId}
        estimateLineItems={estimateLineItems}
        targetLineItemIndex={targetLineItemIndex}
        initialFocusMeasurementId={initialFocusMeasurementId}
        onLinked={({ estimate, measurement, line_item_index }) => {
          // Backend has already updated the estimate line quantities. Bubble
          // the linked line back to EstimateEditor so its local form state
          // refreshes without a manual reload.
          const li = (estimate?.line_items || [])[line_item_index] || {};
          const output_value = li.quantity ?? measurement.square_feet ?? measurement.linear_feet ?? measurement.metric_value ?? 0;
          onApply?.({
            measurement_id: measurement.id,
            output_value,
            label: measurement.notes || measurement.service_category || defaultLabel || "Measurement",
            measurement,
            estimate,
            line_item_index,
          });
          onClose?.();
        }}
        onSaved={(m) => {
          // In estimate context we do NOT auto-apply on save — user must
          // press "Add to Estimate" to link. In standalone context (no
          // estimateId) fall back to the pre-D.1 behavior of returning the
          // saved measurement to the caller.
          if (estimateId) return;
          const output_value = m.square_feet ?? m.linear_feet ?? m.metric_value ?? 0;
          onApply?.({
            measurement_id: m.id,
            output_value,
            label: m.service_category || defaultLabel || "Measurement",
            measurement: m,
          });
          onClose?.();
        }}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="line-measure-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Ruler className="w-5 h-5 text-[#FF5A00]" />
            {isEdit ? "Edit measurement" : "Measure on map"}
            {defaultLabel ? <span className="text-sm text-zinc-500 font-normal">· {defaultLabel}</span> : null}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isLinear
              ? "Draw a line along the feature (fence, gutter, edging). Length auto-fills the line's quantity."
              : "Draw a polygon around the area (roof, driveway, lawn). Square footage auto-fills the line's quantity."}
            {" "}You can override the value manually below.
          </DialogDescription>
        </DialogHeader>

        <PropertyMapPanel
          initialAddress={propertyAddress}
          initialGeoJson={rawGeomToFC(existingMeasurement?.geometry)}
          onChange={(m) => setDrawn({
            areaSqFt: m.areaSqFt,
            lengthFt: m.lengthFt,
            inferredAreaSqFt: m.inferredAreaSqFt || 0,
            geojson: m.geojson,
          })}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">
              Name this measurement
            </Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Roof · House Zone A · Driveway · Fence"
              data-testid="line-measure-label"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-500">
              Manual override ({unitLabel})
            </Label>
            <Input
              type="number"
              step="0.01"
              value={manualOverride}
              onChange={(e) => setManualOverride(e.target.value)}
              placeholder={`Drawn: ${drawnValue.toLocaleString()} ${unitLabel}`}
              data-testid="line-measure-override"
            />
          </div>
        </div>

        <div className="mt-3 text-sm text-zinc-700 bg-zinc-50 border border-zinc-200 rounded px-3 py-2" data-testid="line-measure-effective">
          Will apply <strong>{effectiveValue.toLocaleString()} {unitLabel}</strong> to this line.
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="line-measure-cancel-btn">
            Cancel
          </Button>
          <Button
            className="bg-[#FF5A00] hover:bg-[#E04F00] text-white"
            disabled={saving}
            onClick={submit}
            data-testid="line-measure-apply-btn"
          >
            {saving ? "Saving…" : isEdit ? "Save & re-apply" : "Save & apply to line"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
